import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiUser, FiMapPin, FiCalendar, FiSettings, FiLock, FiStar, FiPackage } from 'react-icons/fi';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './Dashboard.css';

export default function Dashboard() {
  const { user, updateProfile, updatePassword } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [profileForm, setProfileForm] = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const res = await API.get('/api/bookings/my');
        setBookings(res.data.data || []);
      } catch {
        setBookings([]);
      } finally {
        setLoadingBookings(false);
      }
    };
    fetchBookings();
  }, []);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    const result = await updateProfile(profileForm);
    toast[result.success ? 'success' : 'error'](result.success ? 'Profile updated!' : result.message);
    setSaving(false);
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { toast.error('New passwords do not match'); return; }
    setSaving(true);
    const result = await updatePassword(passwordForm.currentPassword, passwordForm.newPassword);
    if (result.success) {
      toast.success('Password updated successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } else {
      toast.error(result.message);
    }
    setSaving(false);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <FiUser /> },
    { id: 'bookings', label: 'My Bookings', icon: <FiPackage /> },
    { id: 'profile', label: 'Edit Profile', icon: <FiSettings /> },
    { id: 'security', label: 'Security', icon: <FiLock /> },
  ];

  const STATUS_COLORS = {
    pending: { background: '#fff3cd', color: '#856404' },
    confirmed: { background: '#d1edff', color: '#004085' },
    cancelled: { background: '#f8d7da', color: '#721c24' },
    completed: { background: '#d4edda', color: '#155724' },
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-hero">
        <div className="container">
          <motion.div className="dashboard-header" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="user-info-bar">
              <div className="user-big-avatar">{user?.name?.charAt(0).toUpperCase()}</div>
              <div>
                <h1>Welcome, {user?.name?.split(' ')[0]}!</h1>
                <p>{user?.email} &middot; {user?.role === 'admin' ? 'Admin' : 'Explorer'}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container">
        <div className="dashboard-layout">
          <div className="dashboard-nav">
            {tabs.map(tab => (
              <button key={tab.id} className={`dash-nav-btn ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          <div className="dashboard-content">
            {activeTab === 'overview' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2>Dashboard Overview</h2>
                <div className="overview-stats">
                  <div className="ov-stat">
                    <div className="ov-icon"><FiPackage /></div>
                    <div><strong>{bookings.length}</strong><span>Total Bookings</span></div>
                  </div>
                  <div className="ov-stat">
                    <div className="ov-icon"><FiMapPin /></div>
                    <div><strong>{bookings.filter(b => b.status === 'completed').length}</strong><span>Trips Completed</span></div>
                  </div>
                  <div className="ov-stat">
                    <div className="ov-icon"><FiCalendar /></div>
                    <div><strong>{bookings.filter(b => b.status === 'pending' || b.status === 'confirmed').length}</strong><span>Upcoming Trips</span></div>
                  </div>
                  <div className="ov-stat">
                    <div className="ov-icon"><FiStar /></div>
                    <div><strong>Explorer</strong><span>Your Status</span></div>
                  </div>
                </div>

                {bookings.length > 0 ? (
                  <div className="recent-bookings">
                    <h3>Recent Bookings</h3>
                    {bookings.slice(0, 3).map((booking) => (
                      <div key={booking._id} className="booking-item">
                        <div>
                          <strong>{booking.trip?.title || 'Trip'}</strong>
                          <p>{booking.trip?.destination} &middot; {booking.travelers} traveler{booking.travelers > 1 ? 's' : ''}</p>
                        </div>
                        <div className="booking-right">
                          <span className="booking-status" style={STATUS_COLORS[booking.status] || {}}>{booking.status}</span>
                          <p className="booking-amount">&#8377;{booking.totalAmount?.toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-bookings">
                    <FiPackage style={{ fontSize: '3rem', color: 'var(--text-light)', display: 'block', margin: '0 auto 16px' }} />
                    <h3>No Bookings Yet</h3>
                    <p>You have not made any bookings yet. Explore our trips and book your next adventure!</p>
                    <a href="/trips" className="btn-primary">Explore Trips</a>
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'bookings' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2>My Bookings</h2>
                {loadingBookings ? (
                  <div className="spinner" style={{ margin: '40px auto', width: 40, height: 40, border: '3px solid rgba(26,71,42,0.2)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                ) : bookings.length === 0 ? (
                  <div className="empty-bookings">
                    <FiPackage style={{ fontSize: '3rem', color: 'var(--text-light)', display: 'block', margin: '0 auto 16px' }} />
                    <h3>No Bookings Yet</h3>
                    <p>You have not made any bookings yet. Explore our trips and book your next adventure!</p>
                    <a href="/trips" className="btn-primary">Explore Trips</a>
                  </div>
                ) : (
                  <div className="bookings-list">
                    {bookings.map((booking) => (
                      <div key={booking._id} className="booking-card">
                        <div className="booking-card-content">
                          <h4>{booking.trip?.title || 'Trip'}</h4>
                          <p className="booking-dest"><FiMapPin /> {booking.trip?.destination}</p>
                          <div className="booking-details">
                            <span><FiCalendar /> {new Date(booking.travelDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            <span><FiUser /> {booking.travelers} traveler{booking.travelers > 1 ? 's' : ''}</span>
                          </div>
                        </div>
                        <div className="booking-card-right">
                          <span className="booking-status" style={STATUS_COLORS[booking.status] || {}}>{booking.status}</span>
                          <strong>&#8377;{booking.totalAmount?.toLocaleString()}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'profile' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2>Edit Profile</h2>
                <form className="settings-form" onSubmit={handleProfileUpdate}>
                  <div className="sf-group">
                    <label>Full Name</label>
                    <input type="text" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} required />
                  </div>
                  <div className="sf-group">
                    <label>Email Address</label>
                    <input type="email" value={user?.email} disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                    <small>Email cannot be changed</small>
                  </div>
                  <div className="sf-group">
                    <label>Phone Number</label>
                    <input type="tel" placeholder="+91 XXXXX XXXXX" value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} />
                  </div>
                  <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
                </form>
              </motion.div>
            )}

            {activeTab === 'security' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2>Security Settings</h2>
                <form className="settings-form" onSubmit={handlePasswordUpdate}>
                  <div className="sf-group">
                    <label>Current Password</label>
                    <input type="password" placeholder="Enter current password" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} required />
                  </div>
                  <div className="sf-group">
                    <label>New Password</label>
                    <input type="password" placeholder="Enter new password (min 6 chars)" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} required minLength={6} />
                  </div>
                  <div className="sf-group">
                    <label>Confirm New Password</label>
                    <input type="password" placeholder="Confirm new password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} required />
                  </div>
                  <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Updating...' : 'Update Password'}</button>
                </form>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
